import { useState } from "react";

import {
  ArrowButton,
  Button,
  TextField,
  Select,
  InfoPanel,
  Typography,
  Modal
} from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { getInstitutionFromOibRequest, getUserFromOibRequest, createUserRequest, createInstitutionHeaderRequest } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { Institution, institutionInitial } from "../../models/Institution";
import { UserInfo, userInfoInitial } from "../../models/User";
import { OibValidationScheme, UserValidationScheme } from "../../models/User";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetUserRolesQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { userRoleRoute, createUserRoute } from "../../routes/routes";
import styles from "./UserRole.module.scss";

export function CreateUser() {
  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const { data: userRoles } = useGetUserRolesQuery();
  const { data: userSession } = useGetUserSessionQuery();

  const getDefaultRole = () => {
    if (userSession) {

      if (userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector || userSession.roleId === RoleEnum.Helpdesk) {
        return RoleEnum.InstitutionAdmin;
      }
      if (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin) {
        return RoleEnum.ASOO;
      }
      if (userSession.roleId === RoleEnum.ASOOOffice) {
        return RoleEnum.ASOOOffice;
      }
      if (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin) {
        return RoleEnum.MZOM;
      }
      if (userSession.roleId === RoleEnum.MZOMOffice) {
        return RoleEnum.MZOMOffice;
      }

    }
    return RoleEnum.InstitutionAdmin;
  };

  const [userFromServer, setUserFromServer] = useState<UserInfo>(userInfoInitial(0, 0));
  const [institution, setInstitution] = useState<Institution>(institutionInitial);
  const [institutionOib, setInstitutionOib] = useState({ oib: "" });
  let defaultRole = getDefaultRole();
  let defaultInstitutionId = userSession && userSession.roleId === RoleEnum.InstitutionAdmin ? userSession.institutionId! : 0;
  const [userData, setUserData] = useState<UserInfo>(userInfoInitial(defaultRole, defaultInstitutionId));
  const [confirmEnabled, setConfirmButton] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false);
  const [userFound, setUserFound] = useState(false);
  const [institutionNotFound, setInstitutionNotFound] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const [institutionErrors, doInstitutionValidations, stopInstitutionValidations] = useValidation({
    data: institutionOib,
    validationSchema: OibValidationScheme
  });

  const [userErrors, doUserValidations, stopUserValidations] = useValidation({
    data: userData,
    validationSchema: UserValidationScheme
  });

  const onBackToUserList = () => {
    history.push(userRoleRoute());
  };

  const handleConfirmedSuccessModal = () => {
    setOpenSuccessModal(false)
    history.push(createUserRoute());
  }

  const onCheckUserData = () => {
    if (!userSession) {
      return;
    }

    var validateInstitution = (userSession.roleId === RoleEnum.Helpdesk) && (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector);
    const isValid = (!validateInstitution || doInstitutionValidations()) && doUserValidations();
    if (!isValid) {
      return;
    }
    const fetchUser = async () => {
      var fetchInstitution = (userSession.roleId === RoleEnum.Helpdesk) && (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector);
      if (fetchInstitution) {
        var institutionData = await getInstitutionFromOibRequest(institutionOib.oib);
        if (institutionData.name) {
          setInstitution(institutionData);
          setInstitutionNotFound(false);
        }
        else {
          setInstitutionNotFound(true);
        }
      }

      var userInfo = await getUserFromOibRequest(userData.oib, userData.firstName, userData.lastName);
      setUserFromServer(userInfo);

      if (userInfo && userInfo.firstName && userInfo.lastName) {
        var canSave = userInfo.roleId === 0 || ((userInfo.roleId === RoleEnum.InstitutionAdmin || userInfo.roleId === RoleEnum.InstitutionDirector) && userInfo.institutionId !== institution.id);
        setUserNotFound(false);
        userData.firstName = userInfo.firstName;
        userData.lastName = userInfo.lastName;
        setUserData(userData);
        setUserFound(canSave);
        setConfirmButton(canSave);
      }
      else {
        setUserFound(false);
        setUserNotFound(true);
        setConfirmButton(false);
      }
    }
    fetchUser();
  };

  const onSaveUser = () => {

    if (!userSession) {
      return;
    }
    var validateInstitution = userSession.roleId === RoleEnum.Helpdesk && (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector);
    const isValid = (!validateInstitution || doInstitutionValidations()) && doUserValidations();

    if (!isValid) {
      return;
    }
    const createInstitutionAndUser = async () => {
      if (institution.oib?.length > 0 && (userSession.roleId !== RoleEnum.InstitutionAdmin && (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector) && institution.id === 0)) {
        userData.institutionId = await createInstitutionHeaderRequest(institution.oib);

        createNewUser();
      }
      else {
        if (userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector) {
          userData.institutionId = userSession.institutionId!;
        }
        createNewUser();
      }
    }
    const createNewUser = async () => {
      await createUserRequest(userData).then(() => {
        setUserFromServer(userInfoInitial(0, 0));
        setInstitution(institutionInitial);
        setUserData(userInfoInitial(userData.roleId, userData.institutionId));
        setInstitutionOib({ oib: "" });
        setUserFound(false);
        stopInstitutionValidations();
        stopUserValidations();
        setOpenSuccessModal(true);
      }).catch((err) => {
        console.log(err);
      })
    };

    if (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector) {
      userData.institutionId = institution.id;
    }
    if (userSession.roleId === RoleEnum.InstitutionDirector) {
      if (userSession.institutionId) {
        userData.institutionId = userSession.institutionId;
      }
    }
    createInstitutionAndUser();
    setUserFromServer(userInfoInitial(0, 0));
  }

  const getUserRoles = () => {
    if (userSession?.roleId === RoleEnum.InstitutionDirector) {
      return userRoles?.filter(ur => ur.id === RoleEnum.InstitutionAdmin || ur.id === RoleEnum.InstitutionDirector);
    }
    if (userSession?.roleId === RoleEnum.MZOMAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.MZOM || ur.id === RoleEnum.MZOMAdmin);
    }
    if (userSession?.roleId === RoleEnum.ASOOAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.ASOO || ur.id === RoleEnum.ASOOAdmin);
    }
    if (userSession?.roleId === RoleEnum.Helpdesk) {
      return userRoles;
    }
    return undefined;
  }

  const getRoleName = (roleId: number) => {

    switch (roleId) {
      case RoleEnum.MZOM:
        return t("userRole.roleMZO");
      case RoleEnum.MZOMAdmin:
        return t("userRole.roleMZOManager");
      case RoleEnum.MZOMOffice:
        return t("userRole.roleMZOOffice");
      case RoleEnum.ASOO:
        return t("userROle.roleASO");
      case RoleEnum.ASOOAdmin:
        return t("userRole.roleASOOManager");
      case RoleEnum.ASOOOffice:
        return t("userRole.roleASOOOffice");
      case RoleEnum.Helpdesk:
        return t("userRole.roleHelpdesk");
    }
    return "";
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <ArrowButton
            backwards
            ariaLabel={t("userRole.backToUserList")}
            onClick={onBackToUserList}
          />
          <span className={styles.backButtonCaption}>
            {t("userRole.backToUserList")}
          </span>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <h4 className={styles.newRoleTitle}>{t("userRole.newRoleTitle")}</h4>
        </Col>
      </Row>

      {userFromServer && userFromServer.roleId !== 0 && userFromServer.roleId !== 1 &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("userRole.userHasRoleMessage", { role: getRoleName(userFromServer.roleId) })}
              color="warning"
              fullWidth
            />
          </Col>
        </Row>}
      {userFromServer && userFromServer.roleId === 1 &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("userRole.userHasInstitutionRoleMessage", { institution: userFromServer.institutionName! })}
              color="warning"
              fullWidth
            />
          </Col>
        </Row>}


      {institutionNotFound && (userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector) &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("userRole.institutionNotFound", { oib: institutionOib.oib.toString().padStart(11, "0") })}
              color="warning"
              fullWidth
            />
          </Col>
        </Row>}
      {userNotFound &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("userRole.userNotFound", { oib: userData.oib.toString().padStart(11, "0") })}
              color="warning"
              fullWidth
            />
          </Col>
        </Row>}
      {userFound &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("userRole.userFound", { oib: userData.oib.toString().padStart(11, "0") })}
              color="success"
              fullWidth
            />
          </Col>
        </Row>}

      <div className={styles.newRolePanel}>
        {userSession && userSession.roleId === RoleEnum.Helpdesk &&
          <Row>
            <Col md={4}>
              <TextField
                label={t("institution.institutionOib")}
                value={(userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector) ? institutionOib.oib : ""}
                name={nameof<typeof institutionOib>("oib")}
                onChange={handleChange(setInstitutionOib)}
                maxLength={11}
                type="number"
                errorText={institutionErrors?.oib?.toString()}
                className={styles.transparentTextLabel}
                disabled={userData.roleId !== RoleEnum.InstitutionAdmin && userData.roleId !== RoleEnum.InstitutionDirector}
              />
            </Col>
            <Col md={8}>
              <TextField
                plaintext
                readonly
                label={t("institution.institutionName")}
                value={userData.roleId === RoleEnum.InstitutionAdmin || userData.roleId === RoleEnum.InstitutionDirector ? institution.name : ""}
                className={styles.transparentTextLabel}
                disabled={userData.roleId !== RoleEnum.InstitutionAdmin && userData.roleId !== RoleEnum.InstitutionDirector}
              />
            </Col>
          </Row>
        }
        <Row>
          <Col md={4}>
            <TextField
              label={t("userRole.oib")}
              name={nameof<typeof userData>("oib")}
              value={userData.oib}
              maxLength={11}
              onChange={handleChange(setUserData)}
              className={styles.transparentTextLabel}
              errorText={userErrors?.oib?.toString()}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.firstName")}
              name={nameof<typeof userData>("firstName")}
              value={userData.firstName}
              maxLength={32}
              onChange={handleChange(setUserData)}
              className={styles.transparentTextLabel}
              errorText={userErrors?.firstName!}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.lastName")}
              name={nameof<typeof userData>("lastName")}
              value={userData.lastName}
              maxLength={32}
              onChange={handleChange(setUserData)}
              className={styles.transparentTextLabel}
              errorText={userErrors?.lastName!}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("userRole.phone")}
              value={userData?.phone!}
              maxLength={80}
              name={nameof<typeof userData>("phone")}
              onChange={handleChange(setUserData)}
              errorText={userErrors?.phone?.toString()}
              className={styles.transparentTextLabel}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("userRole.email")}
              value={userData?.email!}
              maxLength={255}
              name={nameof<typeof userData>("email")}
              onChange={handleChange(setUserData)}
              errorText={userErrors?.email?.toString()}
              className={styles.transparentTextLabel}
            />
          </Col>
        </Row>
        {userSession && (userSession.roleId === RoleEnum.Helpdesk || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.ASOOAdmin || userSession.roleId === RoleEnum.InstitutionDirector) &&
          <Row>
            <Col md={4}>
              <Select
                label={t("common.userRole")}
                name={nameof<typeof userData>("roleId")}
                value={userData.roleId}
                onChange={handleChange(setUserData)}
                selectData={codeBookToSelectData(getUserRoles())}
                className={styles.transparentTextLabel}
              />
            </Col>
          </Row>
        }
        <Row>
          <Col md={4}>
            <Button outline onClick={onCheckUserData}>
              {t("userRole.checkUserData")}
            </Button>
          </Col>
        </Row>
      </div>
      <Row>
        <Col md={12}>
          <Button onClick={onSaveUser} className={styles.actionButton}>
            {t("userRole.addUserRole")}
          </Button>
        </Col>
      </Row>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModal}
        onConfirm={handleConfirmedSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>

          <Typography component="h1" variant="largeBold">
            {t("common.saveSucceed")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}
